// @ts-strict-ignore
import * as React from 'react';
import { DisallowedTokensForBranch, ReleaseType, requireAtLeastOneOfTokensForBranch } from '../types';
import { ModalProps } from 'antd/lib/modal';
import Modal from 'antd/lib/modal/Modal';
import { flatMap } from 'lodash';
import Form, { Rule } from 'antd/lib/form';
import { validatePattern } from '../helpers';
import Select from 'antd/lib/select';
import useForm from 'antd/lib/form/hooks/useForm';
import FormItem from 'antd/lib/form/FormItem';
import Input from 'antd/lib/input';
import { RootState } from '../reducers/reducers';
import { useSelector } from 'react-redux';
import { AppComponentProps } from '../typescriptHelpers';
import { selectApp } from '../selectors/selectors';
import Button from 'antd/lib/button';
import Spin from 'antd/lib/spin';
import { runwayColors } from './styles/globalStyles';
import Alert from 'antd/lib/alert';
import Text from 'antd/lib/typography/Text';
import Typography from 'antd/lib/typography';
import TooltipInfoIcon from './helperComponents/TooltipInfoIcon';

interface NewReleaseBranchPatternModalProps extends AppComponentProps {
    open: boolean;
    isLoading: boolean;
    onCancelAddNewReleaseBranchPattern: ModalProps['onCancel'];
    onOkNewReleaseBranchPattern: (pattern: string, isHotfix: boolean) => void;
    suggestedBranchPattern: string;
    suggestedBranchPatternIsLoading: boolean;
}

const NewReleaseBranchPatternModal = (props: NewReleaseBranchPatternModalProps) => {
    const existingPatterns = useSelector((state: RootState) => {
        const app = selectApp(state, props.appId);

        return app.appSettings.releaseBranchPatterns;
    });

    const hasExistingNonHotfixPattern = existingPatterns.some((pattern) => !pattern.isHotfix);
    const isHotfixDisabled = !hasExistingNonHotfixPattern || existingPatterns.some((pattern) => pattern.isHotfix);
    const isNonHotfixDisabled = existingPatterns.some((pattern) => !pattern.isHotfix);
    const initialReleaseType = isNonHotfixDisabled && !isHotfixDisabled ? ReleaseType.Hotfix : null;

    const [shouldDisplaySuggestedBranchLabel, setShouldDisplaySuggestedBranchLabel] = React.useState(false);

    const [shouldDisplaySuggestedBranchSpinner, setShouldDisplaySuggestedBranchSpinner] = React.useState(false);

    React.useEffect(() => {
        if (props.suggestedBranchPattern !== '') {
            const showSuggestion = !existingPatterns.some(
                (pattern) => props.suggestedBranchPattern === pattern.pattern,
            );
            setShouldDisplaySuggestedBranchLabel(showSuggestion);
        }
    }, [props.suggestedBranchPattern]);

    React.useEffect(() => {
        setShouldDisplaySuggestedBranchSpinner(props.suggestedBranchPatternIsLoading);
    }, [props.suggestedBranchPatternIsLoading]);

    const updatedBranchPatternRules = (releaseType?: ReleaseType) => {
        const rules: Rule[] = [{ required: true, message: 'Branch pattern is required.' }];
        const isHotfix = releaseType === ReleaseType.Hotfix;
        const doesPatternOfSameTypeExist = existingPatterns.some((existing) => existing.isHotfix == isHotfix);
        const doesVersionedPatternExistForNonHotfix = existingPatterns.some(
            (existing) => !existing.isHotfix && !existing.isStatic,
        );
        const shouldBeVersioned = doesPatternOfSameTypeExist || (doesVersionedPatternExistForNonHotfix && isHotfix);

        // validate pattern
        rules.push({
            validator: (rule, value) => {
                const pattern = value as string;

                return validatePattern(
                    pattern,
                    'Branch',
                    requireAtLeastOneOfTokensForBranch(shouldBeVersioned),
                    DisallowedTokensForBranch,
                );
            },
        });

        // check against existing
        rules.push({
            validator: (rule, value) => {
                const pattern = value as string;

                if (existingPatterns.some((existing) => existing.pattern == pattern)) {
                    return Promise.reject('Identical branch pattern already exists.');
                }

                return Promise.resolve();
            },
        });

        return rules;
    };

    const { Option } = Select;
    const [formIsValid, setFormIsValid] = React.useState(false);
    const [branchPatternRules, setBranchPatternRules] = React.useState(updatedBranchPatternRules(initialReleaseType));
    const [form] = useForm();

    const onOk = () => {
        form.validateFields().then((values) => {
            const isHotfix = values.releaseType === ReleaseType.Hotfix;
            const selectedPattern = values.branchPattern;

            props.onOkNewReleaseBranchPattern(selectedPattern, isHotfix);
        });
    };

    const onFieldsChange = () => {
        const errors = flatMap(form.getFieldsError().map((field) => field.errors));

        setFormIsValid(errors.length === 0);
    };

    const onCancel = (e) => {
        props.onCancelAddNewReleaseBranchPattern(e);
    };

    const onApplySuggestionButton = () => {
        form.setFieldsValue({ branchPattern: props.suggestedBranchPattern });
        onFieldsChange();
    };

    React.useEffect(() => {
        if (props.open) {
            form.validateFields(['branchPattern']);
        }
    }, [branchPatternRules.length]);

    return (
        <Modal
            title="New release branch pattern"
            open={props.open}
            onCancel={onCancel}
            onOk={onOk}
            okText="Create release branch pattern"
            confirmLoading={props.isLoading}
            okButtonProps={{ loading: props.isLoading, disabled: !formIsValid }}
            afterClose={() => {
                form.resetFields();
                setFormIsValid(false);
                const initialRules = updatedBranchPatternRules(initialReleaseType);
                setBranchPatternRules(initialRules);
            }}
            destroyOnClose={true}
        >
            <Form
                form={form}
                name="newReleaseBranchPattern"
                labelCol={{ span: 10 }}
                wrapperCol={{ span: 16, offset: 1 }}
                labelAlign="left"
                autoComplete="off"
                onFieldsChange={onFieldsChange}
                initialValues={{
                    releaseType: initialReleaseType,
                    branchPattern: null,
                }}
            >
                <FormItem label="Release type" name="releaseType">
                    <Select
                        placeholder="Select"
                        onChange={(value) => {
                            const updatedRules = updatedBranchPatternRules(value as ReleaseType);

                            setBranchPatternRules(updatedRules);
                        }}
                    >
                        <Option key="none" value={null} disabled={isNonHotfixDisabled}>
                            All types
                        </Option>
                        <Option key={ReleaseType.Hotfix} value={ReleaseType.Hotfix} disabled={isHotfixDisabled}>
                            Hotfixes
                        </Option>
                    </Select>
                </FormItem>
                <FormItem label="Release branch pattern" name="branchPattern" rules={branchPatternRules}>
                    <Input placeholder="e.g. release-{version}" autoComplete="off" />
                </FormItem>
                <Text type="secondary">
                    Read more about tokenized patterns in our{' '}
                    <Typography.Link
                        href="https://docs.runway.team/getting-started/pattern-strings-tokens"
                        target="_blank"
                    >
                        docs.
                    </Typography.Link>
                </Text>
                {shouldDisplaySuggestedBranchSpinner && (
                    <p>
                        <Spin size="small" />
                    </p>
                )}
                {shouldDisplaySuggestedBranchLabel && (
                    <Alert
                        style={{
                            marginTop: '1em',
                            marginBottom: '1em',
                            backgroundColor: `${runwayColors.primary}10`,
                            border: `1px solid ${runwayColors.primary}50`,
                        }}
                        message={
                            <>
                                Suggested:{' '}
                                <Text code style={{ margin: '1em' }}>
                                    {props.suggestedBranchPattern}
                                </Text>
                                <TooltipInfoIcon tooltipContent="Suggestion for this field based on the branches we found in your repository" />
                            </>
                        }
                        action={
                            <Button size="small" onClick={onApplySuggestionButton}>
                                Apply
                            </Button>
                        }
                    />
                )}
            </Form>
        </Modal>
    );
};

export default NewReleaseBranchPatternModal;
